import { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { SectionState } from '../../UrgencySummary/useSectionData'

interface OperatingRoomSectionProps<T> {
  /** Nombre de la sección para el lector de pantalla (el título de la pestaña). */
  label: string
  /** La consulta de la pestaña (aparte, recién al abrirla o al cambiar los filtros). */
  state: SectionState<T>
  /** Qué no se pudo consultar («No se pudieron consultar los tiempos. Intentá de nuevo.»). */
  errorMessageId: string
  children: (data: T) => ReactNode
}

/**
 * El marco de una pestaña que se consulta aparte: cargando (solo la primera vez), error con «Reintentar» (nunca una pestaña
 * vacía que parezca «sin datos») y, con datos, el contenido; mientras llega una consulta nueva se sigue viendo lo anterior,
 * desenfocado y con el aviso de «Cargando…» de la pantalla.
 */
export default function OperatingRoomSection<T>({ label, state, errorMessageId, children }: OperatingRoomSectionProps<T>) {
  const intl = useIntl()
  const loaded = state.data ?? state.stale
  const refreshing = state.status === 'loading'

  return (
    <section className="nv-or-group" aria-label={label} aria-busy={refreshing}>
      {refreshing && loaded === null && (
        <span className="nv-loading" role="status">
          <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
          <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
        </span>
      )}
      {state.status === 'failed' && (
        <div className="nv-or-failed" role="alert">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: errorMessageId })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={state.retry} />
        </div>
      )}
      {loaded !== null && state.status !== 'failed' && (
        <div className={`nv-or-group__body${refreshing ? ' nv-or-blurred' : ''}`}>{children(loaded)}</div>
      )}
    </section>
  )
}
